import {
    useContext,
    useEffect,
    useRef,
    type FC
} from "react";
import {
    Animated,
    View
} from "react-native";
import type IDataTableSelectionBarProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import DataTableSelectionBarStandalone from "./standalone";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useDataTableSelectionBarContext,
    useDataTableSelectionContext,
    useDataTableContext,
    DataTableContext
} from "../../context";
import {
    Trash2 as DeleteIcon,
    X as ClearIcon
} from "lucide-react-native";
import {
    getSlideStyle
} from "../../../../helpers/motion";
import {
    formatNumber
} from "../../../../helpers/locale";
import DataTableExport from "../export";
import Button from "../../../button";
import Text from "../../../text";

const DataTableAttachedSelectionBar: FC<IDataTableSelectionBarProps> = ({
    customTheme: customThemeProp,
    isExportHidden = false,
    children,
    style,
    ...props
}) => {
    const context = useDataTableContext({
        part: "SelectionBar"
    });

    const selectionContext = useDataTableSelectionContext();

    const selectionBarContext = useDataTableSelectionBarContext();

    const {
        controlHeights,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const {
        container: containerDynamicStyle,
        count: countDynamicStyle
    } = useStyles({
        toolbarLayout: selectionBarContext?.toolbarLayout,
        controlHeights,
        colors,
        spaces
    });

    const registerSelectionBar = context?.registerSelectionBar;

    const selectedCountRef = useRef(0);

    useEffect(() => {
        return registerSelectionBar?.();
    }, [
        registerSelectionBar
    ]);

    if(!context || !selectionContext || !selectionBarContext?.isSelectionBarActive) {
        return null;
    }

    if(selectionContext.selectedRowIds.length > 0) {
        selectedCountRef.current = selectionContext.selectedRowIds.length;
    }

    const selectedCount = selectedCountRef.current;

    const isOverlay = !!selectionBarContext.toolbarLayout;

    const renderClearIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <ClearIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const renderDeleteIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <DeleteIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    return <Animated.View
        aria-label={localize("data-table-selection-group")}
        {...props}
        style={[
            style,
            stylesheet.container,
            isOverlay ? stylesheet.overlay : null,
            containerDynamicStyle,
            getSlideStyle({
                isReduceMotionEnabled: context.isReduceMotionEnabled,
                progress: selectionBarContext.selectionBarProgress,
                offset: isOverlay ? 0 : spaces.spacingSm
            })
        ]}
        role="group"
    >
        <View
            style={[
                stylesheet.count,
                countDynamicStyle
            ]}
            aria-live="polite"
            role="status"
        >
            <Text
                customTheme={customThemeProp ?? context.customTheme}
                variant="labelMediumSize"
                color="high"
            >
                {
                    localize(selectedCount === 1 ? "data-table-selected-count-one" : "data-table-selected-count", [
                        formatNumber({
                            value: selectedCount,
                            locale: activeLocale
                        })
                    ])
                }
            </Text>
        </View>
        {
            selectionContext.isAllSelected && !selectionContext.isAllFilteredSelected && context.selectionBehaviour === "multiple" ?
                <Button
                    title={
                        localize("data-table-select-all-count", [
                            formatNumber({
                                value: context.allFilteredRowIds.length,
                                locale: activeLocale
                            })
                        ])
                    }
                    customTheme={customThemeProp ?? context.customTheme}
                    onPress={context.handleSelectAllRows}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            :
                null
        }
        {children}
        <View
            style={stylesheet.spacer}
        />
        {
            isExportHidden ?
                null
            :
                <DataTableExport
                    customTheme={customThemeProp ?? context.customTheme}
                    scope="selected"
                    variant="ghost"
                />
        }
        {
            context.isSelectionDeletable ?
                <Button
                    isDisabled={context.isPermissionDenied || context.isLoading}
                    customTheme={customThemeProp ?? context.customTheme}
                    onPress={context.handleDeleteSelection}
                    title={localize("data-table-delete")}
                    icon={renderDeleteIcon}
                    variant="ghost"
                    type="danger"
                    size="small"
                />
            :
                null
        }
        <Button
            customTheme={customThemeProp ?? context.customTheme}
            accessibilityLabel={localize("clear-selection")}
            onPress={context.handleClearSelection}
            icon={renderClearIcon}
            spreadBehaviour="free"
            variant="ghost"
            type="neutral"
            size="small"
        />
    </Animated.View>;
};

const DataTableSelectionBar: FC<IDataTableSelectionBarProps> = ({
    accessibilityLabel,
    selectedCount,
    bottomOffset,
    customTheme,
    isVisible,
    actions,
    onClose,
    style,
    ...props
}) => {
    const context = useContext(DataTableContext);

    if(!context && selectedCount !== undefined) {
        return <DataTableSelectionBarStandalone
            accessibilityLabel={accessibilityLabel ?? props["aria-label"]}
            selectedCount={selectedCount}
            bottomOffset={bottomOffset}
            customTheme={customTheme}
            isVisible={isVisible}
            onClose={onClose}
            actions={actions}
            style={style}
        />;
    }

    return <DataTableAttachedSelectionBar
        accessibilityLabel={accessibilityLabel}
        customTheme={customTheme}
        style={style}
        {...props}
    />;
};
export default DataTableSelectionBar;
